import mapDocToEntry from "@/components/Entry/Layouts/mapDocToEntry.ts";
import {
  CategoryDocument,
  Category,
  PartialCategoryDocument,
  PartialCategory,
} from "@/types/category.ts";
import { DEFAULT_DATE_FORMAT } from "@/util/constants.ts";
import type { HomeCarouselCategory } from "@/types/site.ts";
import { categoryToAttributeConfig } from "@/util/categoryToAttributeConfig.ts";
import type { Store } from "vuex";
import type { RootState } from "@/store/types.ts";
import { transformMedia } from "@/util/mediaHelper.v2.ts";

function mapDocExceptSubDocs(
  doc: CategoryDocument | PartialCategoryDocument,
  isTouchScreen: boolean
) {
  return {
    background: (doc.background ?? []).map(transformMedia),
    cardBackground: (doc.cardBackground ?? []).map(transformMedia),
    defaultPersonCardBackground: (doc.defaultPersonCardBackground ?? []).map(
      transformMedia
    ),
    layout: doc.layout,
    recordColumns: doc.recordColumns ?? [],
    hideFolderCardNames: doc.hideFolderCardNames ?? false,
    dateFormat: doc.dateFormat ?? DEFAULT_DATE_FORMAT,
    _id: doc._id ?? doc.legacyId,
    categoryDescription: {
      content: doc.categoryDescription,
      options: { disableExternalLinks: isTouchScreen },
    },
    attributesCategories: doc.attributesCategories,
    filterCategories: doc.filterCategories,
    categoryName: doc.categoryName,
    eventDateAlternativeName: doc.eventDateAlternativeName,
    graduationDateAlternativeName: doc.graduationDateAlternativeName,
    activityAlternativeName: doc.activityAlternativeName,
    typeAlternativeName: doc.typeAlternativeName,
    slideshowImage1AlternativeName: doc.slideshowImage1AlternativeName,
    slideshowVideoAlternativeName: doc.slideshowVideoAlternativeName,
    isLive: doc.isLive,
    path: doc.path,
    imageObjectFit: doc.imageObjectFit,
    index: doc.index,
    parentCategoryID: doc.parentCategoryID,
    showNavigationButtons: doc.showNavigationButtons,
    hasSubCategories: doc.hasSubCategories,
  };
}

function mapDocToPartialCategory(
  doc: PartialCategoryDocument,
  isTouchScreen: boolean
): PartialCategory {
  return {
    entries: doc.entries ?? [],
    categories: doc.categories ?? [],
    ...mapDocExceptSubDocs(doc, isTouchScreen),
  };
}

function mapDocToCategory(
  doc: CategoryDocument,
  isTouchScreen: boolean
): Category {
  return {
    entries: (doc.entries ?? []).map((i) =>
      mapDocToEntry(i, isTouchScreen, doc.dateFormat)
    ),
    categories: (doc.categories ?? []).map((i) =>
      mapDocToPartialCategory(i, isTouchScreen)
    ),
    ...mapDocExceptSubDocs(doc, isTouchScreen),
  };
}

function mapDocToHomeCarouselCategory(
  doc: CategoryDocument,
  isTouchScreen: boolean,
  store: Store<RootState>
): HomeCarouselCategory {
  const baseCategory = mapDocToCategory(doc, isTouchScreen);
  const attributeConfig = categoryToAttributeConfig(baseCategory, store);
  return {
    ...mapDocExceptSubDocs(doc, isTouchScreen),
    categories: (doc.categories ?? []).map((i) =>
      mapDocToPartialCategory(i, isTouchScreen)
    ),
    entries: (doc.entries ?? []).map((i) => ({
      ...mapDocToEntry(i, isTouchScreen, doc.dateFormat),
      attributeConfig,
      dateFormat: doc.dateFormat ?? baseCategory.dateFormat,
    })),
  };
}

export {
  mapDocToCategory,
  mapDocToPartialCategory,
  mapDocToHomeCarouselCategory,
};
